import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, getAuthToken, removeAuthToken, updateAppBadge } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Phone, Mail, Building2, ArrowLeft, Plus, Search, Filter, UserPlus, Bell, CheckCircle2, Users as UsersIcon, MessageCircle, FileText, Linkedin, Edit, Trash2, Briefcase, MapPin, DollarSign, Image, X, ChevronRight, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';

const Contacts = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [leadSpecialists, setLeadSpecialists] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isCallLogOpen, setIsCallLogOpen] = useState(false);
  const [callNotes, setCallNotes] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [contactToAssign, setContactToAssign] = useState(null);
  const [selectedSpecialist, setSelectedSpecialist] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [availabilityStatus, setAvailabilityStatus] = useState('available');
  const [allUsers, setAllUsers] = useState([]);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [referredCandidates, setReferredCandidates] = useState([]);
  const [referredSearch, setReferredSearch] = useState('');
  const [eligibilityFilter, setEligibilityFilter] = useState('All');
  const [callingStatusFilter, setCallingStatusFilter] = useState('All');
  const [editingReferredCandidate, setEditingReferredCandidate] = useState(null);
  
  // Job Updates State
  const [jobUpdates, setJobUpdates] = useState([]);
  const [isJobPanelOpen, setIsJobPanelOpen] = useState(false);
  const [isJobDialogOpen, setIsJobDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [newJob, setNewJob] = useState({
    company_name: '',
    position_name: '',
    job_description: '',
    requirements: '',
    salary_range: '',
    location: '',
    company_logo_url: '',
    job_image_url: ''
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingJobImage, setUploadingJobImage] = useState(false);
  
  const [newContact, setNewContact] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    years_of_experience: '',
    skills: '',
    highest_qualification: '',
    apply_to_company: '',
    assigned_to_specialist: '',
    status: 'New Lead',
    notes: ''
  });

  const statusColors = {
    'New Lead': 'bg-blue-500',
    'Contacted': 'bg-yellow-500',
    'Follow-up': 'bg-orange-500',
    'Converted': 'bg-green-500',
    'Lost': 'bg-gray-500',
    'Interviewed': 'bg-purple-500',
    'Hired': 'bg-emerald-600',
    'Rejected': 'bg-red-500'
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterContacts();
  }, [contacts, searchQuery, statusFilter]);

  // Update app badge when notifications change
  useEffect(() => {
    const unreadCount = notifications.filter(n => !n.read).length;
    updateAppBadge(unreadCount);
  }, [notifications]);

  const fetchData = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [userRes, contactsRes, usersRes, notifRes, unreadRes, referredRes, jobsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, config),
        axios.get(`${API}/contacts`, config),
        axios.get(`${API}/users`, config),
        axios.get(`${API}/notifications`, config),
        axios.get(`${API}/messages/unread/count`, config).catch(() => ({ data: { unread_count: 0 } })),
        axios.get(`${API}/referred-candidates`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/job-updates`, config).catch(() => ({ data: [] }))
      ]);

      setCurrentUser(userRes.data);
      setContacts(contactsRes.data);
      setNotifications(notifRes.data);
      setAvailabilityStatus(userRes.data.availability_status || 'available');
      setAllUsers(usersRes.data);
      setUnreadMessageCount(unreadRes.data.unread_count || 0);
      setReferredCandidates(referredRes.data);
      setJobUpdates(jobsRes.data);
      
      // Filter lead specialists
      const specialists = usersRes.data.filter(u => u.role === 'lead_specialist');
      setLeadSpecialists(specialists);
    } catch (error) {
      if (error.response?.status === 401) {
        removeAuthToken();
        navigate('/login');
      }
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = contacts;

    if (searchQuery) {
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.phone.includes(searchQuery) ||
          (c.email && c.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (c.company && c.company.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (statusFilter !== 'All') {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }

    setFilteredContacts(filtered);
  };

  const updateAvailabilityStatus = async (status) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/users/availability?status=${status}`, {}, config);
      setAvailabilityStatus(status);
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const availabilityOptions = [
    { value: 'available', label: 'Available', color: 'from-emerald-500 to-green-500', icon: '🟢' },
    { value: 'offline', label: 'Offline', color: 'from-gray-500 to-slate-600', icon: '⚫' },
    { value: 'meeting', label: 'Meeting', color: 'from-blue-500 to-indigo-600', icon: '🔵' },
    { value: 'training', label: 'Training', color: 'from-amber-500 to-orange-500', icon: '🟡' },
    { value: 'system', label: 'System', color: 'from-red-500 to-rose-600', icon: '🔴' }
  ];

  const currentAvailability = availabilityOptions.find(a => a.value === availabilityStatus) || availabilityOptions[0];

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API}/contacts`, newContact, config);
      toast.success('Contact added successfully!');
      setIsAddDialogOpen(false);
      setNewContact({
        name: '',
        phone: '',
        email: '',
        company: '',
        years_of_experience: '',
        skills: '',
        highest_qualification: '',
        apply_to_company: '',
        assigned_to_specialist: '',
        status: 'New Lead',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    }
  };

  const handleUpdateStatus = async (contactId, newStatus) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.patch(`${API}/contacts/${contactId}`, { status: newStatus }, config);
      toast.success('Status updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleCall = async (contact) => {
    setSelectedContact(contact);
    setIsCallLogOpen(true);
    
    // Open phone dialer
    window.location.href = `tel:${contact.phone}`;
  };

  const handleAssignToSpecialist = async () => {
    if (!contactToAssign || !selectedSpecialist) {
      toast.error('Please select a specialist');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.patch(
        `${API}/contacts/${contactToAssign.id}`,
        { assigned_to_specialist: selectedSpecialist },
        config
      );
      
      const specialist = leadSpecialists.find(s => s.email === selectedSpecialist);
      toast.success(`${contactToAssign.name} assigned to ${specialist?.name || selectedSpecialist}`);
      setAssignDialogOpen(false);
      setContactToAssign(null);
      setSelectedSpecialist('');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign candidate');
    }
  };

  const handleLogCall = async () => {
    if (!selectedContact) return;

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(
        `${API}/contacts/${selectedContact.id}/log-call?notes=${encodeURIComponent(callNotes)}`,
        {},
        config
      );
      
      toast.success('Call logged successfully!');
      setIsCallLogOpen(false);
      setCallNotes('');
      setSelectedContact(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to log call');
    }
  };

  // Referred Candidates functions
  const updateReferredCandidate = async (candidateId, updates) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.patch(`${API}/referred-candidates/${candidateId}`, updates, config);
      toast.success('Candidate updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update candidate');
    }
  };

  const filteredReferredCandidates = referredCandidates.filter(candidate => {
    const searchLower = referredSearch.toLowerCase();
    const matchesSearch = !referredSearch || 
      candidate.name?.toLowerCase().includes(searchLower) ||
      candidate.email?.toLowerCase().includes(searchLower) ||
      candidate.phone?.includes(searchLower) ||
      candidate.referred_by_name?.toLowerCase().includes(searchLower);
    
    const matchesEligibility = eligibilityFilter === 'All' || candidate.eligible === eligibilityFilter;
    const matchesCalling = callingStatusFilter === 'All' || candidate.calling_status === callingStatusFilter;
    
    return matchesSearch && matchesEligibility && matchesCalling;
  });

  // Job Updates Functions
  const canEditJobs = currentUser && currentUser.role !== 'lead_specialist';

  const handleUploadJobImage = async (file, type) => {
    if (type === 'logo') setUploadingLogo(true);
    else setUploadingJobImage(true);
    
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', type);
      
      const response = await axios.post(`${API}/job-updates/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (type === 'logo') {
        setNewJob(prev => ({ ...prev, company_logo_url: response.data.image_url }));
      } else {
        setNewJob(prev => ({ ...prev, job_image_url: response.data.image_url }));
      }
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      if (type === 'logo') setUploadingLogo(false);
      else setUploadingJobImage(false);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    if (!newJob.company_name || !newJob.position_name || !newJob.job_description) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editingJob) {
        await axios.patch(`${API}/job-updates/${editingJob.id}`, newJob, config);
        toast.success('Job update updated!');
      } else {
        await axios.post(`${API}/job-updates`, newJob, config);
        toast.success('Job update created!');
      }
      
      setIsJobDialogOpen(false);
      setEditingJob(null);
      setNewJob({
        company_name: '',
        position_name: '',
        job_description: '',
        requirements: '',
        salary_range: '',
        location: '',
        company_logo_url: '',
        job_image_url: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setNewJob({
      company_name: job.company_name,
      position_name: job.position_name,
      job_description: job.job_description,
      requirements: job.requirements || '',
      salary_range: job.salary_range || '',
      location: job.location || '',
      company_logo_url: job.company_logo_url || '',
      job_image_url: job.job_image_url || ''
    });
    setIsJobDialogOpen(true);
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/job-updates/${jobId}`, config);
      toast.success('Job deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-gradient-to-r from-indigo-900 via-blue-900 to-indigo-900 sticky top-0 z-50 shadow-xl">
        <div className="max-w-7xl mx-auto px-2 md:px-6 py-2 md:py-3 flex items-center justify-between">
          {/* Back + Logo + App Name */}
          <div className="flex items-center gap-1.5 md:gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-button" className="h-7 w-7 md:h-9 md:w-9 text-white hover:bg-white/10">
              <ArrowLeft className="h-4 w-4 md:h-5 md:w-5" />
            </Button>
            <img 
              src="/logo.png?v=15" 
              alt="Officium Connect" 
              className="h-8 md:h-10 rounded-lg"
              data-testid="crm-logo"
            />
            <span className="font-heading font-bold text-sm md:text-xl tracking-tight whitespace-nowrap text-white">
              Officium Connect
            </span>
          </div>

          {/* Availability + Notification + User Info */}
          <div className="flex items-center gap-1 md:gap-3">
            {/* Availability Status Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  className={`rounded-lg border-2 bg-gradient-to-r ${currentAvailability.color} text-white border-transparent hover:opacity-90 transition-all shadow-md h-7 md:h-9 px-2`}
                  data-testid="crm-availability-btn"
                >
                  <span className="mr-1">{currentAvailability.icon}</span>
                  <span className="hidden sm:inline text-xs">{currentAvailability.label}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="p-2">
                  <h3 className="font-heading font-semibold text-sm mb-2">Set Status</h3>
                  <div className="space-y-1">
                    {availabilityOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => updateAvailabilityStatus(option.value)}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 transition-all text-sm ${
                          availabilityStatus === option.value ? 'bg-slate-100 font-medium' : ''
                        }`}
                      >
                        <span>{option.icon}</span>
                        <span>{option.label}</span>
                        {availabilityStatus === option.value && (
                          <CheckCircle2 className="h-4 w-4 ml-auto text-emerald-500" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Team Directory */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 md:h-9 md:w-9 text-white hover:bg-white/10" data-testid="crm-team-directory-btn">
                  <UsersIcon className="h-4 w-4 md:h-5 md:w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                <div className="p-3">
                  <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
                    <UsersIcon className="h-4 w-4" />
                    Team Directory
                    <Badge variant="outline" className="ml-auto text-xs">{allUsers.length}</Badge>
                  </h3>
                  {allUsers.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No team members</p>
                  ) : (
                    <div className="space-y-2">
                      {allUsers.map((user) => {
                        const userStatus = availabilityOptions.find(a => a.value === user.availability_status) || availabilityOptions[0];
                        return (
                          <div key={user.id} className="p-2 rounded-lg border hover:bg-slate-50 transition-colors">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate">{user.name}</p>
                                <p className="text-xs text-muted-foreground font-mono truncate">{user.email}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-xs bg-gradient-to-r ${userStatus.color} text-white`}>
                                {userStatus.label}
                              </span>
                            </div>
                            {user.phone && (
                              <a 
                                href={`tel:${user.phone}`}
                                className="mt-2 flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800"
                                onClick={() => toast.info(`Calling ${user.name}...`)}
                              >
                                <Phone className="h-3 w-3" />
                                <span className="font-mono">{user.phone}</span>
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Messages */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative h-7 w-7 md:h-9 md:w-9 text-white hover:bg-white/10"
              onClick={() => navigate('/')}
              data-testid="crm-messages-btn"
            >
              <MessageCircle className="h-4 w-4 md:h-5 md:w-5" />
              {unreadMessageCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center font-mono">
                  {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                </span>
              )}
            </Button>

            {/* Job Updates Button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative h-7 w-7 md:h-9 md:w-9 text-white hover:bg-white/10"
              onClick={() => setIsJobPanelOpen(true)}
              data-testid="crm-job-updates-btn"
            >
              <Briefcase className="h-4 w-4 md:h-5 md:w-5" />
              {jobUpdates.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-mono">
                  {jobUpdates.length > 9 ? '9+' : jobUpdates.length}
                </span>
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-7 w-7 md:h-9 md:w-9 text-white hover:bg-white/10" data-testid="crm-notifications-btn">
                  <Bell className="h-4 w-4 md:h-5 md:w-5" />
                  {notifications.filter(n => !n.read).length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-mono">
                      {notifications.filter(n => !n.read).length > 9 ? '9+' : notifications.filter(n => !n.read).length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <div className="p-3">
                  <h3 className="font-heading font-semibold text-sm mb-2">Notifications</h3>
                  {notifications.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">No notifications</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {notifications.slice(0, 5).map((notif) => (
                        <div key={notif.id} className={`p-2 rounded-sm text-xs ${notif.read ? 'opacity-60' : 'bg-blue-50'}`}>
                          <p>{notif.message}</p>
                          <p className="text-muted-foreground font-mono text-[10px] mt-1">{format(new Date(notif.created_at), 'MMM dd, h:mm a')}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <p className="text-xs font-medium hidden md:block text-white">{currentUser?.name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        <Tabs defaultValue="contacts" className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="contacts" className="text-sm">
              <UsersIcon className="h-4 w-4 mr-2" />
              CRM Contacts
            </TabsTrigger>
            <TabsTrigger value="referred" className="text-sm">
              <UserPlus className="h-4 w-4 mr-2" />
              Referred Candidates
              {referredCandidates.length > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">{referredCandidates.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="space-y-4">
            <div className="mb-4 md:mb-6 flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-4 flex-1">
                <div className="relative flex-1 md:max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search name, phone, email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 rounded-sm text-sm"
                    data-testid="search-input"
                  />
                </div>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-36 md:w-40 rounded-sm text-sm" data-testid="status-filter">
                    <Filter className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Status</SelectItem>
                    <SelectItem value="New Lead">New Lead</SelectItem>
                    <SelectItem value="Contacted">Contacted</SelectItem>
                    <SelectItem value="Follow-up">Follow-up</SelectItem>
                    <SelectItem value="Interviewed">Interviewed</SelectItem>
                    <SelectItem value="Hired">Hired</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                    <SelectItem value="Converted">Converted</SelectItem>
                    <SelectItem value="Lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-sm font-medium w-full sm:w-auto text-sm" data-testid="add-contact-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Contact
                  </Button>
                </DialogTrigger>
            <DialogContent className="w-[calc(100vw-2rem)] md:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Add New Contact</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddContact} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Current Company</Label>
                    <Input
                      id="company"
                      value={newContact.company}
                      onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="years_of_experience">Years of Experience</Label>
                    <Input
                      id="years_of_experience"
                      type="number"
                      min="0"
                      value={newContact.years_of_experience}
                      onChange={(e) => setNewContact({ ...newContact, years_of_experience: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="highest_qualification">Highest Qualification</Label>
                    <Input
                      id="highest_qualification"
                      placeholder="e.g., MBA, B.Tech"
                      value={newContact.highest_qualification}
                      onChange={(e) => setNewContact({ ...newContact, highest_qualification: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="skills">Skills</Label>
                  <Input
                    id="skills"
                    placeholder="e.g., React, Python, Sales"
                    value={newContact.skills}
                    onChange={(e) => setNewContact({ ...newContact, skills: e.target.value })}
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="apply_to_company">Want to Apply In Company</Label>
                  <Input
                    id="apply_to_company"
                    placeholder="Company they want to join"
                    value={newContact.apply_to_company}
                    onChange={(e) => setNewContact({ ...newContact, apply_to_company: e.target.value })}
                    className="rounded-sm"
                  />
                </div>


                <div className="space-y-2">
                  <Label htmlFor="assigned_to_specialist">Assign to Lead Specialist (Optional)</Label>
                  <Select
                    value={newContact.assigned_to_specialist || "none"}
                    onValueChange={(value) => setNewContact({ ...newContact, assigned_to_specialist: value === "none" ? "" : value })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Select specialist" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (Unassigned)</SelectItem>
                      {leadSpecialists.map((specialist) => (
                        <SelectItem key={specialist.id} value={specialist.email}>
                          {specialist.name} ({specialist.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>


                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={newContact.status}
                    onValueChange={(value) => setNewContact({ ...newContact, status: value })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New Lead">New Lead</SelectItem>
                      <SelectItem value="Contacted">Contacted</SelectItem>
                      <SelectItem value="Follow-up">Follow-up</SelectItem>
                      <SelectItem value="Interviewed">Interviewed</SelectItem>
                      <SelectItem value="Hired">Hired</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                      <SelectItem value="Converted">Converted</SelectItem>
                      <SelectItem value="Lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={newContact.notes}
                    onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                    className="rounded-sm"
                    rows={3}
                  />
                </div>

                <Button type="submit" className="w-full rounded-sm font-medium">
                  Add Contact
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-3 md:gap-4" data-testid="contacts-list">
          {filteredContacts.length === 0 ? (
            <div className="border border-border rounded-sm p-8 md:p-12 text-center">
              <p className="text-muted-foreground text-sm">
                {searchQuery || statusFilter !== 'All'
                  ? 'No contacts found matching your criteria'
                  : 'No contacts yet. Add your first contact!'}
              </p>
            </div>
          ) : (
            filteredContacts.map((contact) => (
              <div
                key={contact.id}
                className="border border-border rounded-sm bg-card p-3 md:p-6 hover:bg-zinc-50/50 transition-colors"
                data-testid={`contact-card-${contact.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 md:gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-heading text-sm md:text-lg font-semibold">{contact.name}</h3>
                      <Badge className={`${statusColors[contact.status]} text-white font-mono text-[10px] md:text-xs rounded-sm`}>
                        {contact.status}
                      </Badge>
                    </div>

                    <div className="space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 md:gap-6 text-xs md:text-sm">
                        <div className="flex items-center gap-2">
                          <Phone className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                          <a
                            href={`tel:${contact.phone}`}
                            className="font-mono text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            data-testid={`phone-link-${contact.id}`}
                            onClick={() => {
                              toast.info(`Calling ${contact.name}...`);
                              setSelectedContact(contact);
                              setTimeout(() => setIsCallLogOpen(true), 2000);
                            }}
                          >
                            {contact.phone}
                          </a>
                        </div>
                        {contact.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                            <a
                              href={`mailto:${contact.email}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[150px] md:max-w-none"
                            >
                              {contact.email}
                            </a>
                          </div>
                        )}
                        {contact.company && (
                          <div className="flex items-center gap-2 hidden sm:flex">
                            <Building2 className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                            <span>{contact.company}</span>
                          </div>
                        )}
                      </div>

                      {contact.notes && (
                        <p className="text-xs md:text-sm text-muted-foreground">{contact.notes}</p>
                      )}

                      <div className="flex flex-wrap items-center gap-1 md:gap-2 text-[10px] md:text-xs text-muted-foreground mt-2">
                        {contact.years_of_experience && (
                          <span className="bg-secondary px-1.5 md:px-2 py-0.5 md:py-1 rounded">
                            <strong>Exp:</strong> {contact.years_of_experience}y
                          </span>
                        )}
                        {contact.highest_qualification && (
                          <span className="bg-secondary px-1.5 md:px-2 py-0.5 md:py-1 rounded hidden sm:inline">
                            <strong>Qual:</strong> {contact.highest_qualification}
                          </span>
                        )}
                        {contact.skills && (
                          <span className="bg-secondary px-1.5 md:px-2 py-0.5 md:py-1 rounded hidden md:inline">
                            <strong>Skills:</strong> {contact.skills}
                          </span>
                        )}
                        {contact.apply_to_company && (
                          <span className="bg-blue-50 text-blue-700 px-1.5 md:px-2 py-0.5 md:py-1 rounded">
                            <strong>Apply:</strong> {contact.apply_to_company}
                          </span>
                        )}
                      </div>

                      {contact.last_contacted && (
                        <p className="text-[10px] md:text-xs text-muted-foreground font-mono mt-2">
                          Last: {format(new Date(contact.last_contacted), 'MMM dd, h:mm a')}
                        </p>
                      )}

                      {contact.assigned_to_specialist && (
                        <div className="mt-2">
                          <Badge variant="outline" className="text-[10px] md:text-xs bg-purple-50 text-purple-700 border-purple-200">
                            <UserPlus className="h-3 w-3 mr-1" />
                            <span className="hidden sm:inline">Assigned to: </span>
                            {leadSpecialists.find(s => s.email === contact.assigned_to_specialist)?.name || contact.assigned_to_specialist}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-stretch md:items-end gap-2">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <Select
                        value={contact.status}
                        onValueChange={(value) => handleUpdateStatus(contact.id, value)}
                      >
                        <SelectTrigger className="w-full sm:w-32 md:w-36 rounded-sm text-xs md:text-sm" data-testid={`status-select-${contact.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="New Lead">New Lead</SelectItem>
                          <SelectItem value="Contacted">Contacted</SelectItem>
                          <SelectItem value="Follow-up">Follow-up</SelectItem>
                          <SelectItem value="Interviewed">Interviewed</SelectItem>
                          <SelectItem value="Hired">Hired</SelectItem>
                          <SelectItem value="Rejected">Rejected</SelectItem>
                          <SelectItem value="Converted">Converted</SelectItem>
                          <SelectItem value="Lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        onClick={() => handleCall(contact)}
                        className="rounded-sm bg-green-600 hover:bg-green-700 text-xs md:text-sm"
                        data-testid={`call-button-${contact.id}`}
                      >
                        <Phone className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                        Call
                      </Button>
                    </div>
                    
                    {currentUser?.is_admin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setContactToAssign(contact);
                          setSelectedSpecialist(contact.assigned_to_specialist || '');
                          setAssignDialogOpen(true);
                        }}
                        className="rounded-sm text-xs w-full sm:w-auto"
                        data-testid={`assign-btn-${contact.id}`}
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        {contact.assigned_to_specialist ? 'Reassign' : 'Assign'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
          </TabsContent>

          {/* Referred Candidates Tab */}
          <TabsContent value="referred" className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-4 flex-1">
                <div className="relative flex-1 md:max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, phone, email, referrer..."
                    value={referredSearch}
                    onChange={(e) => setReferredSearch(e.target.value)}
                    className="pl-10 rounded-sm text-sm"
                  />
                </div>
                
                <Select value={eligibilityFilter} onValueChange={setEligibilityFilter}>
                  <SelectTrigger className="w-full sm:w-32 rounded-sm text-sm">
                    <SelectValue placeholder="Eligibility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Eligibility</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Yes">Eligible</SelectItem>
                    <SelectItem value="No">Not Eligible</SelectItem>
                    <SelectItem value="Hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={callingStatusFilter} onValueChange={setCallingStatusFilter}>
                  <SelectTrigger className="w-full sm:w-36 rounded-sm text-sm">
                    <SelectValue placeholder="Calling Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Status</SelectItem>
                    <SelectItem value="Not Called">Not Called</SelectItem>
                    <SelectItem value="Called">Called</SelectItem>
                    <SelectItem value="Not Available">Not Available</SelectItem>
                    <SelectItem value="Rescheduled">Rescheduled</SelectItem>
                    <SelectItem value="Not Interested">Not Interested</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {filteredReferredCandidates.length === 0 ? (
              <div className="border border-border rounded-lg p-12 text-center bg-white">
                <UserPlus className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No referred candidates found</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredReferredCandidates.map(candidate => (
                  <Card key={candidate.id} className="rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                        {/* Candidate Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold text-lg">{candidate.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                Referred by <span className="font-medium">{candidate.referred_by_name}</span>
                                {candidate.created_at && (
                                  <span className="ml-2 text-xs">• {format(new Date(candidate.created_at), 'MMM dd, yyyy')}</span>
                                )}
                              </p>
                              {(candidate.refer_to_company || candidate.position) && (
                                <p className="text-sm mt-1">
                                  <span className="font-medium text-emerald-600">For: {candidate.refer_to_company}</span>
                                  {candidate.position && <span className="text-muted-foreground"> • {candidate.position}</span>}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Badge variant={
                                candidate.eligible === 'Yes' ? 'default' : 
                                candidate.eligible === 'No' ? 'destructive' : 
                                candidate.eligible === 'Hold' ? 'secondary' : 'outline'
                              }>
                                {candidate.eligible}
                              </Badge>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
                            <div>
                              <p className="text-muted-foreground text-xs">Phone</p>
                              <a 
                                href={`tel:${candidate.phone}`} 
                                className="font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                onClick={() => toast.info(`Calling ${candidate.name}...`)}
                              >
                                <Phone className="h-3 w-3" />
                                {candidate.phone}
                              </a>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Email</p>
                              <a href={`mailto:${candidate.email}`} className="font-medium text-blue-600 hover:text-blue-800 truncate block">
                                {candidate.email}
                              </a>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Experience</p>
                              <p className="font-medium">{candidate.experience || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Qualification</p>
                              <p className="font-medium">{candidate.qualification || 'N/A'}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                            <div>
                              <p className="text-muted-foreground text-xs">Current Company</p>
                              <p className="font-medium">{candidate.current_company || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Notice Period</p>
                              <p className="font-medium">{candidate.notice_period || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Location</p>
                              <p className="font-medium">{candidate.country_state || 'N/A'}</p>
                            </div>
                            {candidate.linkedin_profile && (
                              <div>
                                <p className="text-muted-foreground text-xs">LinkedIn</p>
                                <a href={candidate.linkedin_profile} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                  <Linkedin className="h-3 w-3" />
                                  Profile
                                </a>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2 mt-3">
                            <Badge variant="outline" className="text-xs">
                              Aadhar: {candidate.has_aadhar_card}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              PAN: {candidate.has_pan_card}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              Aadhar-Mobile: {candidate.aadhar_linked_mobile}
                            </Badge>
                            {candidate.resume_link && (
                              <a href={candidate.resume_link} target="_blank" rel="noopener noreferrer">
                                <Badge variant="secondary" className="text-xs cursor-pointer hover:bg-secondary/80">
                                  <FileText className="h-3 w-3 mr-1" />
                                  Link
                                </Badge>
                              </a>
                            )}
                            {candidate.cv_file_id && (
                              <a 
                                href={`${API}/cv/${candidate.cv_file_id}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  // Download with auth
                                  const token = getAuthToken();
                                  fetch(`${API}/cv/${candidate.cv_file_id}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  })
                                  .then(res => res.blob())
                                  .then(blob => {
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `CV_${candidate.name.replace(/\s+/g, '_')}.pdf`;
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                  })
                                  .catch(() => toast.error('Failed to download CV'));
                                }}
                              >
                                <Badge variant="default" className="text-xs cursor-pointer bg-emerald-500 hover:bg-emerald-600">
                                  <FileText className="h-3 w-3 mr-1" />
                                  Download CV
                                </Badge>
                              </a>
                            )}
                          </div>
                        </div>

                        {/* CRM Actions */}
                        <div className="flex flex-col gap-2 min-w-[200px]">
                          <div className="space-y-2">
                            <Label className="text-xs">Calling Status</Label>
                            <Select 
                              value={candidate.calling_status} 
                              onValueChange={(value) => updateReferredCandidate(candidate.id, { calling_status: value })}
                            >
                              <SelectTrigger className="rounded-sm text-sm h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Not Called">Not Called</SelectItem>
                                <SelectItem value="Called">Called</SelectItem>
                                <SelectItem value="Not Available">Not Available</SelectItem>
                                <SelectItem value="Rescheduled">Rescheduled</SelectItem>
                                <SelectItem value="Not Interested">Not Interested</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-xs">Eligible</Label>
                            <Select 
                              value={candidate.eligible} 
                              onValueChange={(value) => updateReferredCandidate(candidate.id, { eligible: value })}
                            >
                              <SelectTrigger className="rounded-sm text-sm h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Pending">Pending</SelectItem>
                                <SelectItem value="Yes">Yes</SelectItem>
                                <SelectItem value="No">No</SelectItem>
                                <SelectItem value="Hold">Hold</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-xs">Email Sent</Label>
                            <Select 
                              value={candidate.email_sent} 
                              onValueChange={(value) => updateReferredCandidate(candidate.id, { email_sent: value })}
                            >
                              <SelectTrigger className="rounded-sm text-sm h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="No">No</SelectItem>
                                <SelectItem value="Yes">Yes</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => {
                              window.location.href = `tel:${candidate.phone}`;
                              toast.info(`Calling ${candidate.name}...`);
                            }}
                            className="w-full mt-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                          >
                            <Phone className="h-4 w-4 mr-2" />
                            Click to Call
                          </Button>
                        </div>
                      </div>

                      {/* Feedback Section */}
                      <div className="mt-4 pt-4 border-t">
                        <Label className="text-xs text-muted-foreground">Feedback / Notes</Label>
                        <Textarea
                          placeholder="Add feedback or notes about this candidate..."
                          value={candidate.feedback || ''}
                          onChange={(e) => updateReferredCandidate(candidate.id, { feedback: e.target.value })}
                          className="mt-1 rounded-sm text-sm"
                          rows={2}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Assign to Specialist Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign to Lead Specialist</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Assign <strong>{contactToAssign?.name}</strong> to a lead specialist
            </p>
            <div className="space-y-2">
              <Label>Select Lead Specialist</Label>
              <Select value={selectedSpecialist} onValueChange={setSelectedSpecialist}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Choose a specialist..." />
                </SelectTrigger>
                <SelectContent>
                  {leadSpecialists.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">No lead specialists available</div>
                  ) : (
                    leadSpecialists.map((specialist) => (
                      <SelectItem key={specialist.id} value={specialist.email}>
                        {specialist.name} ({specialist.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleAssignToSpecialist} 
              className="w-full rounded-sm"
              disabled={!selectedSpecialist}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Assign Candidate
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Call Log Dialog */}
      <Dialog open={isCallLogOpen} onOpenChange={setIsCallLogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Log Call - {selectedContact?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="call-notes">Call Notes</Label>
              <Textarea
                id="call-notes"
                placeholder="What was discussed? Any follow-up needed?"
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                className="rounded-sm"
                rows={4}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setIsCallLogOpen(false)}
                variant="outline"
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button onClick={handleLogCall} className="flex-1 rounded-sm">
                Save Call Log
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Updates Side Panel */}
      {isJobPanelOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setIsJobPanelOpen(false)}
          />
          
          {/* Side Panel */}
          <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-indigo-900 via-blue-900 to-indigo-900 text-white">
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                <h2 className="font-heading font-semibold text-lg">Job Updates</h2>
                <Badge variant="secondary" className="bg-white/20 text-white">{jobUpdates.length}</Badge>
              </div>
              <div className="flex items-center gap-2">
                {canEditJobs && (
                  <Button 
                    size="sm" 
                    variant="secondary"
                    className="h-8"
                    onClick={() => {
                      setEditingJob(null);
                      setNewJob({
                        company_name: '',
                        position_name: '',
                        job_description: '',
                        requirements: '',
                        salary_range: '',
                        location: '',
                        company_logo_url: '',
                        job_image_url: ''
                      });
                      setIsJobDialogOpen(true);
                    }}
                    data-testid="add-job-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Job
                  </Button>
                )}
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8 text-white hover:bg-white/10"
                  onClick={() => setIsJobPanelOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Job List */}
            <ScrollArea className="flex-1 p-4">
              {jobUpdates.length === 0 ? (
                <div className="text-center py-12">
                  <Briefcase className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No job updates yet</p>
                  {canEditJobs && (
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setIsJobDialogOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add First Job
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {jobUpdates.map((job) => (
                    <div 
                      key={job.id} 
                      className="p-4 border rounded-xl bg-white shadow-sm hover:shadow-md transition-all cursor-pointer group"
                      onClick={() => setSelectedJob(job)}
                      data-testid={`job-card-${job.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {job.company_logo_url ? (
                          <img 
                            src={`${API.replace('/api', '')}${job.company_logo_url}`}
                            alt={job.company_name}
                            className="h-12 w-12 rounded-lg object-cover border"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                            {job.company_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base truncate">{job.position_name}</h3>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {job.company_name}
                          </p>
                          {job.location && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {job.location}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-blue-500 transition-colors" />
                      </div>
                      {job.salary_range && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-green-600 font-medium">
                          <DollarSign className="h-3 w-3" />
                          {job.salary_range}
                        </div>
                      )}
                      <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{job.job_description}</p>
                      
                      {/* Edit/Delete for Admin/User */}
                      {canEditJobs && (
                        <div className="mt-3 pt-2 border-t flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 h-7 text-xs"
                            onClick={() => handleEditJob(job)}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteJob(job.id)}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Job Detail View Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedJob && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  {selectedJob.company_logo_url ? (
                    <img 
                      src={`${API.replace('/api', '')}${selectedJob.company_logo_url}`}
                      alt={selectedJob.company_name}
                      className="h-16 w-16 rounded-xl object-cover border"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-2xl">
                      {selectedJob.company_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <DialogTitle className="text-xl">{selectedJob.position_name}</DialogTitle>
                    <p className="text-muted-foreground flex items-center gap-1 mt-1">
                      <Building2 className="h-4 w-4" />
                      {selectedJob.company_name}
                    </p>
                  </div>
                </div>
              </DialogHeader>

              {selectedJob.job_image_url && (
                <div className="my-4">
                  <img 
                    src={`${API.replace('/api', '')}${selectedJob.job_image_url}`}
                    alt="Job"
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                </div>
              )}

              <div className="space-y-4">
                {/* Location & Salary */}
                <div className="flex flex-wrap gap-4">
                  {selectedJob.location && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedJob.location}</span>
                    </div>
                  )}
                  {selectedJob.salary_range && (
                    <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
                      <DollarSign className="h-4 w-4" />
                      <span>{selectedJob.salary_range}</span>
                    </div>
                  )}
                </div>

                {/* Job Description */}
                <div>
                  <h4 className="font-semibold text-sm mb-2">Job Description</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedJob.job_description}</p>
                </div>

                {/* Requirements */}
                {selectedJob.requirements && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Requirements</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedJob.requirements}</p>
                  </div>
                )}

                {/* Posted Info */}
                <div className="pt-4 border-t text-xs text-muted-foreground">
                  Posted by {selectedJob.created_by_name} on {new Date(selectedJob.created_at).toLocaleDateString()}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Job Dialog */}
      <Dialog open={isJobDialogOpen} onOpenChange={setIsJobDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingJob ? 'Edit Job Posting' : 'Add New Job Posting'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateJob} className="space-y-4">
            {/* Company Logo */}
            <div>
              <Label className="text-sm">Company Logo</Label>
              <div className="mt-1 flex items-center gap-3">
                {newJob.company_logo_url ? (
                  <img 
                    src={`${API.replace('/api', '')}${newJob.company_logo_url}`}
                    alt="Logo preview"
                    className="h-12 w-12 rounded-lg object-cover border"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <span className="text-sm text-blue-600 hover:text-blue-700">
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </span>
                  <input 
                    type="file" 
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && handleUploadJobImage(e.target.files[0], 'logo')}
                    disabled={uploadingLogo}
                  />
                </label>
              </div>
            </div>

            {/* Company Name */}
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input 
                id="company_name"
                value={newJob.company_name}
                onChange={(e) => setNewJob(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Enter company name"
                required
              />
            </div>

            {/* Position Name */}
            <div>
              <Label htmlFor="position_name">Position Name *</Label>
              <Input 
                id="position_name"
                value={newJob.position_name}
                onChange={(e) => setNewJob(prev => ({ ...prev, position_name: e.target.value }))}
                placeholder="e.g. Senior Software Engineer"
                required
              />
            </div>

            {/* Location */}
            <div>
              <Label htmlFor="location">Location</Label>
              <Input 
                id="location"
                value={newJob.location}
                onChange={(e) => setNewJob(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. New York, Remote"
              />
            </div>

            {/* Salary Range */}
            <div>
              <Label htmlFor="salary_range">Salary Range</Label>
              <Input 
                id="salary_range"
                value={newJob.salary_range}
                onChange={(e) => setNewJob(prev => ({ ...prev, salary_range: e.target.value }))}
                placeholder="e.g. $80,000 - $120,000"
              />
            </div>

            {/* Job Description */}
            <div>
              <Label htmlFor="job_description">Job Description *</Label>
              <Textarea 
                id="job_description"
                value={newJob.job_description}
                onChange={(e) => setNewJob(prev => ({ ...prev, job_description: e.target.value }))}
                placeholder="Describe the role and responsibilities..."
                rows={4}
                required
              />
            </div>

            {/* Requirements */}
            <div>
              <Label htmlFor="requirements">Requirements</Label>
              <Textarea 
                id="requirements"
                value={newJob.requirements}
                onChange={(e) => setNewJob(prev => ({ ...prev, requirements: e.target.value }))}
                placeholder="List the requirements and qualifications..."
                rows={3}
              />
            </div>

            {/* Job Image */}
            <div>
              <Label className="text-sm">Job Image (Optional)</Label>
              <div className="mt-1">
                {newJob.job_image_url ? (
                  <div className="relative inline-block">
                    <img 
                      src={`${API.replace('/api', '')}${newJob.job_image_url}`}
                      alt="Job preview"
                      className="h-24 w-auto rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={() => setNewJob(prev => ({ ...prev, job_image_url: '' }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                      <Image className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">
                        {uploadingJobImage ? 'Uploading...' : 'Click to upload image'}
                      </span>
                    </div>
                    <input 
                      type="file" 
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && handleUploadJobImage(e.target.files[0], 'job')}
                      disabled={uploadingJobImage}
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsJobDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                {editingJob ? 'Update Job' : 'Create Job'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
